import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Case, CaseFromDB, WorkflowStep } from "@/types/workflow";
import { useToast } from "@/hooks/use-toast";

export function useCaseData() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: cases = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["cases"],
    queryFn: async () => {
      console.log("Fetching cases from database");
      const { data, error } = await supabase
        .from("cases")
        .select(`
          *,
          case_attachments(count)
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching cases:", error);
        throw error;
      }

      console.log("Successfully fetched cases:", data);

      return data.map((caseData: CaseFromDB): Case => {
        let parsedWorkflow: WorkflowStep[] = [];
        
        if (caseData.workflow) {
          try {
            const workflowData = Array.isArray(caseData.workflow) 
              ? caseData.workflow 
              : [caseData.workflow];

            parsedWorkflow = workflowData.map((step: any): WorkflowStep => ({
              title: step.title || '',
              description: step.description || '',
              status: step.status || 'pending',
              needs_documentation: step.needs_documentation || false,
              legal_references: step.legal_references || [],
              required_documents: step.required_documents || [],
              estimated_time: step.estimated_time,
              helpful_resources: step.helpful_resources || [],
              document_type: step.document_type,
            }));
          } catch (e) {
            console.error("Error parsing workflow data:", e);
          }
        }

        return {
          id: caseData.id,
          title: caseData.title,
          description: caseData.description,
          status: caseData.status,
          created_at: caseData.created_at,
          updated_at: caseData.updated_at,
          user_id: caseData.user_id,
          workflow: parsedWorkflow,
          template_id: caseData.template_id,
          deadline: caseData.deadline,
          deadline_notification_email: caseData.deadline_notification_email,
          deadline_notification_days: caseData.deadline_notification_days,
        };
      });
    },
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  const updateCase = useMutation({
    mutationFn: async (updates: Partial<CaseFromDB> & { id: string }) => {
      console.log("Updating case:", updates);
      const { data, error } = await supabase
        .from("cases")
        .update(updates)
        .eq("id", updates.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
      toast({
        title: "Sak oppdatert",
        description: "Saken har blitt oppdatert",
        className: "bg-green-50 border-green-200",
      });
    },
    onError: (error) => {
      console.error("Error updating case:", error);
      toast({
        variant: "destructive",
        title: "Feil ved oppdatering",
        description: "Kunne ikke oppdatere saken. Prøv igjen senere.",
      });
    },
  });

  const deleteCase = useMutation({
    mutationFn: async (id: string) => {
      console.log("Deleting case:", id);
      const { error } = await supabase
        .from("cases")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
      toast({
        title: "Sak slettet",
        description: "Saken har blitt slettet",
        className: "bg-amber-50 border-amber-200",
      });
    },
    onError: (error) => {
      console.error("Error deleting case:", error);
      toast({
        variant: "destructive",
        title: "Feil ved sletting",
        description: "Kunne ikke slette saken. Prøv igjen senere.",
      });
    },
  });

  return {
    cases,
    isLoading,
    error,
    refetch,
    updateCase,
    deleteCase,
  };
}